import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from '@common/components/Breadcrumbs';
import Header from '@common/components/Header';
import { Button, TimeRangeFilter } from '@common/components';
import { fetchStatuses } from '@api/statuses';
import { REQUIRED_FILTER_TYPES, REQUIRED_FILTER_TO_ENTITY_MAP } from '@reports/utils/dashboard/constants';
import { convertTimeRangeToDashboardFilters, getDefaultTimeRangeValue } from '@reports/utils/timeRangeUtils';
import HostFilter from '../HostFilter/HostFilter';
import { HostGroupFilter } from '../HostGroupFilter/HostGroupFilter';
import { HostCategoryFilter } from '../HostCategoryFilter/HostCategoryFilter';
import { BusinessServiceFilter } from '../BusinessServiceFilter/BusinessServiceFilter';
import { MetricFilter } from '../MetricFilter/MetricFilter';
import { MetricCategoryFilter } from '../MetricCategoryFilter/MetricCategoryFilter';
import HostStatusFilter from '../HostStatusFilter/HostStatusFilter';
import MetricStatusFilter from '../MetricStatusFilter/MetricStatusFilter';
import * as styles from './ReportFiltersConfiguration.module.css';

/**
 * ReportFiltersConfiguration Component
 * Step 2: Shows only the filters marked as required in the report configuration
 * Allows users to configure time range and entity filters before viewing a report
 *
 * Each entity filter (Host, Host Group, Business Service, Metric) is shown as a separate section.
 * Filters are combined with AND logic when querying data.
 *
 * @param {Object} report - The report configuration
 * @param {function} onGenerate - Callback when generate/regenerate is clicked
 * @param {function} onCancel - Callback when cancel/discard is clicked
 * @param {Object} initialFilters - Initial filter values (for edit mode)
 * @param {boolean} isEditMode - Whether we're editing an existing saved report
 * @param {string} savedReportName - Name of the saved report (for edit mode subtitle)
 */
function ReportFiltersConfiguration({ report, onGenerate, onCancel, initialFilters, isEditMode, savedReportName }) {
  const { t } = useTranslation();
  // Merge initialFilters with defaults to ensure all required properties exist
  // This handles cases where URL params provide partial filters (e.g., entities but no timeRange)
  const [filters, setFilters] = useState(() => {
    const defaults = {
      timeRange: getDefaultTimeRangeValue(),
      entities: {
        hosts: [],
        metrics: [],
        business_services: [],
        host_groups: [],
      },
      hostStatuses: [],
      metricStatuses: [],
      hostCategories: [],
      metricCategories: [],
    };
    if (!initialFilters) return defaults;

    // Restore timeRange object from string label + timeFrom/timeTo
    const { timeRange: rawTimeRange } = initialFilters;
    let resolvedTimeRange = rawTimeRange;
    if (rawTimeRange === 'custom') {
      const toISO = (ts) => ts ? new Date(ts * 1000).toISOString().slice(0, 16) : null;
      resolvedTimeRange = { mode: 'custom', range: null, start: toISO(initialFilters.timeFrom), end: toISO(initialFilters.timeTo), timezone: 'local' };
    } else if (typeof rawTimeRange === 'string') {
      resolvedTimeRange = { mode: 'predefined', range: rawTimeRange, start: null, end: null, timezone: 'local' };
    }

    return {
      ...defaults,
      ...initialFilters,
      timeRange: resolvedTimeRange || defaults.timeRange,
      entities: {
        ...defaults.entities,
        ...initialFilters.entities,
      },
    };
  });
  const [availableStatuses, setAvailableStatuses] = useState({
    hostStatuses: [],
    metricStatuses: [],
  });

  // Memoize requiredFilters to prevent reference changes on every render
  const requiredFilters = useMemo(() => report.requiredFilters || [], [report.requiredFilters]);

  // Simple boolean checks - no need to memoize these as .includes() on small arrays is cheap
  const hasHostFilter = requiredFilters.includes('host');
  const hasHostGroupFilter = requiredFilters.includes('host_group');
  const hasHostCategoryFilter = requiredFilters.includes('host_category');
  const hasBusinessServiceFilter = requiredFilters.includes('business_service');
  const hasMetricFilter = requiredFilters.includes('metric');
  const hasMetricCategoryFilter = requiredFilters.includes('metric_category');
  const hasTimeRangeFilter = requiredFilters.includes('time_range');
  const hasHostStatusFilter = requiredFilters.includes('host_status');
  const hasMetricStatusFilter = requiredFilters.includes('metric_status');

  // Check if any entity type filters are required (slightly more complex, still cheap)
  const hasRequiredEntityFilters = REQUIRED_FILTER_TYPES.some(
    (key) => requiredFilters.includes(key) && REQUIRED_FILTER_TO_ENTITY_MAP[key],
  );

  // Load statuses once on mount and set them as selected by default
  useEffect(() => {
    // Skip if neither status filter is required
    if (!hasHostStatusFilter && !hasMetricStatusFilter) {
      return;
    }

    const loadStatuses = async () => {
      try {
        const statuses = await fetchStatuses();
        setAvailableStatuses(statuses);

        // Set all statuses as selected by default (only if no initial filters provided)
        setFilters((prev) => ({
          ...prev,
          // Only set hostStatuses if host_status filter is required and no initial value
          ...(hasHostStatusFilter &&
            !initialFilters?.hostStatuses && {
              hostStatuses: statuses.hostStatuses.map((state) => state.value),
            }),
          // Only set metricStatuses if metric_status filter is required and no initial value
          ...(hasMetricStatusFilter &&
            !initialFilters?.metricStatuses && {
              metricStatuses: statuses.metricStatuses.map((status) => status.value),
            }),
        }));
      } catch (error) {
        console.error('Error loading statuses:', error);
      }
    };
    loadStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHostStatusFilter, hasMetricStatusFilter]);

  // Handle time range change - memoized
  const handleTimeRangeChange = useCallback((timeRangeValue) => {
    setFilters((prev) => ({
      ...prev,
      timeRange: timeRangeValue,
    }));
  }, []);

  // Handle host selection change - memoized
  const handleHostSelectionChange = useCallback((newSelection) => {
    setFilters((prev) => ({
      ...prev,
      entities: {
        ...prev.entities,
        hosts: newSelection,
      },
    }));
  }, []);

  // Handle host group selection change - memoized
  const handleHostGroupSelectionChange = useCallback((newSelection) => {
    setFilters((prev) => ({
      ...prev,
      entities: {
        ...prev.entities,
        host_groups: newSelection,
      },
    }));
  }, []);

  // Handle host category selection change - memoized
  // This filters report data to only include hosts from selected categories
  const handleHostCategorySelectionChange = useCallback((newSelection) => {
    setFilters((prev) => ({
      ...prev,
      hostCategories: newSelection,
    }));
  }, []);

  // Handle business service selection change - memoized
  const handleBusinessServiceSelectionChange = useCallback((newSelection) => {
    setFilters((prev) => ({
      ...prev,
      entities: {
        ...prev.entities,
        business_services: newSelection,
      },
    }));
  }, []);

  // Handle metric selection change - memoized
  const handleMetricSelectionChange = useCallback((newSelection) => {
    setFilters((prev) => ({
      ...prev,
      entities: {
        ...prev.entities,
        metrics: newSelection,
      },
    }));
  }, []);

  // Handle metric category selection change - memoized
  // This filters report data to only include metrics from selected categories
  const handleMetricCategorySelectionChange = useCallback((newSelection) => {
    setFilters((prev) => ({
      ...prev,
      metricCategories: newSelection,
    }));
  }, []);

  // Handle host statuses change - memoized
  const handleHostStatusesChange = useCallback((states) => {
    setFilters((prev) => ({
      ...prev,
      hostStatuses: states,
    }));
  }, []);

  // Handle metric statuses change - memoized
  const handleMetricStatusesChange = useCallback((statuses) => {
    setFilters((prev) => ({
      ...prev,
      metricStatuses: statuses,
    }));
  }, []);

  const handleGenerate = useCallback(() => {
    // Validate required filters are filled
    const missingFilters = [];

    // Validate time_range if it's required
    if (hasTimeRangeFilter) {
      const { mode, range, start, end } = filters.timeRange;

      if (mode === 'custom') {
        if (!start || !end) {
          missingFilters.push(
            `${t('dashboard.requiredFilters.filters.time_range')} (both start and end dates required)`,
          );
        }
      } else if (mode === 'predefined' && !range) {
        missingFilters.push(t('dashboard.requiredFilters.filters.time_range'));
      }
    }

    // Check entity type filters that are mapped and required
    REQUIRED_FILTER_TYPES.forEach((filterKey) => {
      if (requiredFilters.includes(filterKey)) {
        const entityType = REQUIRED_FILTER_TO_ENTITY_MAP[filterKey];

        if (entityType) {
          if (!filters.entities[entityType] || filters.entities[entityType].length === 0) {
            missingFilters.push(t(`dashboard.requiredFilters.filters.${filterKey}`));
          }
        }
      }
    });

    // Validate host_status if it's required
    if (hasHostStatusFilter && (!filters.hostStatuses || filters.hostStatuses.length === 0)) {
      missingFilters.push(t('dashboard.requiredFilters.filters.host_status'));
    }

    // Validate metric_status if it's required
    if (hasMetricStatusFilter && (!filters.metricStatuses || filters.metricStatuses.length === 0)) {
      missingFilters.push(t('dashboard.requiredFilters.filters.metric_status'));
    }

    // Validate host_category if it's required
    if (hasHostCategoryFilter && (!filters.hostCategories || filters.hostCategories.length === 0)) {
      missingFilters.push(t('dashboard.requiredFilters.filters.host_category'));
    }

    // Validate metric_category if it's required
    if (hasMetricCategoryFilter && (!filters.metricCategories || filters.metricCategories.length === 0)) {
      missingFilters.push(t('dashboard.requiredFilters.filters.metric_category'));
    }

    if (missingFilters.length > 0) {
      // eslint-disable-next-line no-alert
      alert(`Please fill the following required filters:\n${missingFilters.join('\n')}`);
      return;
    }

    // Build the filters object to pass to parent
    // Note: Filters are combined with AND logic in the backend query
    const filtersToPass = {
      entities: filters.entities,
    };

    // Only include time range if it's a required filter
    if (hasTimeRangeFilter) {
      const { timeFrom, timeTo } = convertTimeRangeToDashboardFilters(filters.timeRange);
      filtersToPass.timeFrom = timeFrom;
      filtersToPass.timeTo = timeTo;
      filtersToPass.timeRange = filters.timeRange;
    }

    // Include host status filter if host_status is required
    if (hasHostStatusFilter) {
      filtersToPass.hostStatuses = filters.hostStatuses || [];
    }

    // Include metric status filter if metric_status is required
    if (hasMetricStatusFilter) {
      filtersToPass.metricStatuses = filters.metricStatuses || [];
    }

    // Include host category filter if host_category is required
    if (hasHostCategoryFilter) {
      filtersToPass.hostCategories = filters.hostCategories || [];
    }

    // Include metric category filter if metric_category is required
    if (hasMetricCategoryFilter) {
      filtersToPass.metricCategories = filters.metricCategories || [];
    }

    onGenerate(filtersToPass);
  }, [filters, requiredFilters, onGenerate, t, hasTimeRangeFilter, hasHostStatusFilter, hasMetricStatusFilter, hasHostCategoryFilter, hasMetricCategoryFilter]);

  // Track if we've already auto-generated (to prevent double calls)
  const hasAutoGenerated = useRef(false);

  // If no required filters at all, skip this step and go directly to report
  useEffect(() => {
    if (
      !hasRequiredEntityFilters &&
      !hasTimeRangeFilter &&
      !hasHostStatusFilter &&
      !hasMetricStatusFilter &&
      !hasHostCategoryFilter &&
      !hasMetricCategoryFilter &&
      !hasAutoGenerated.current
    ) {
      hasAutoGenerated.current = true;
      onGenerate(filters);
    }
  }, [hasRequiredEntityFilters, hasTimeRangeFilter, hasHostStatusFilter, hasMetricStatusFilter, hasHostCategoryFilter, hasMetricCategoryFilter, onGenerate, filters]);

  // Don't render the configuration UI if no filters are required at all
  if (!hasRequiredEntityFilters && !hasTimeRangeFilter && !hasHostStatusFilter && !hasMetricStatusFilter && !hasHostCategoryFilter && !hasMetricCategoryFilter) {
    return null;
  }

  return (
    <div className={styles.page}>
      <Breadcrumbs
        items={[
          { label: t('reports.pageTitle'), path: '/' },
          ...(isEditMode ? [{ label: t('savedReports.title'), path: '/saved' }] : []),
          { label: isEditMode ? savedReportName : report.name },
        ]}
      />
      <Header subtitle={isEditMode ? savedReportName : report.name}>{t('reports.pageTitle')}</Header>

      <div className={styles.content}>
        {/* 1. Host Filter */}
        {hasHostFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.host')}</h3>
            <HostFilter
              selectedHostIds={filters.entities.hosts || []}
              onSelectionChange={handleHostSelectionChange}
            />
          </div>
        )}

        {/* 2. Host Group Filter */}
        {hasHostGroupFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.host_group')}</h3>
            <HostGroupFilter
              selectedHostGroupIds={filters.entities.host_groups || []}
              onSelectionChange={handleHostGroupSelectionChange}
            />
          </div>
        )}

        {/* 2.5 Host Category Filter */}
        {hasHostCategoryFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.host_category')}</h3>
            <HostCategoryFilter
              selectedHostCategoryIds={filters.hostCategories || []}
              onSelectionChange={handleHostCategorySelectionChange}
            />
          </div>
        )}

        {/* 3. Business Service Filter */}
        {hasBusinessServiceFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.business_service')}</h3>
            <BusinessServiceFilter
              selectedBusinessServiceIds={filters.entities.business_services || []}
              onSelectionChange={handleBusinessServiceSelectionChange}
            />
          </div>
        )}

        {/* 4. Metric Filter */}
        {hasMetricFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.metric')}</h3>
            <MetricFilter
              selectedMetricIds={filters.entities.metrics || []}
              onSelectionChange={handleMetricSelectionChange}
            />
          </div>
        )}

        {/* 4.5 Metric Category Filter */}
        {hasMetricCategoryFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.metric_category')}</h3>
            <MetricCategoryFilter
              selectedMetricCategoryIds={filters.metricCategories || []}
              onSelectionChange={handleMetricCategorySelectionChange}
            />
          </div>
        )}

        {/* 5. Time Range Filter */}
        {hasTimeRangeFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.time_range')}</h3>
            <TimeRangeFilter value={filters.timeRange} onChange={handleTimeRangeChange} showTimeZone />
          </div>
        )}

        {/* 6. Host Status Filter */}
        {hasHostStatusFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.host_status')}</h3>
            <HostStatusFilter
              hostStatuses={availableStatuses.hostStatuses}
              selectedHostStatuses={filters.hostStatuses}
              onHostStatusesChange={handleHostStatusesChange}
            />
          </div>
        )}

        {/* 7. Metric Status Filter */}
        {hasMetricStatusFilter && (
          <div className={styles.filterCard}>
            <h3 className={styles.filterTitle}>{t('dashboard.requiredFilters.filters.metric_status')}</h3>
            <MetricStatusFilter
              metricStatuses={availableStatuses.metricStatuses}
              selectedMetricStatuses={filters.metricStatuses}
              onMetricStatusesChange={handleMetricStatusesChange}
            />
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {isEditMode ? (
          <>
            <Button variant="text" onClick={onCancel}>
              {t('savedReports.actions.discardChanges')}
            </Button>
            <Button variant="primary" onClick={handleGenerate}>
              {t('savedReports.actions.regenerateReport')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleGenerate}>
              {t('reports.generateReport')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

ReportFiltersConfiguration.propTypes = {
  report: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    requiredFilters: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onGenerate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({
    timeRange: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    timeFrom: PropTypes.number,
    timeTo: PropTypes.number,
    entities: PropTypes.shape({
      hosts: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
      metrics: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
      business_services: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
      host_groups: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    }),
    hostStatuses: PropTypes.arrayOf(PropTypes.string),
    metricStatuses: PropTypes.arrayOf(PropTypes.string),
    hostCategories: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    metricCategories: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  }),
  isEditMode: PropTypes.bool,
  savedReportName: PropTypes.string,
};

ReportFiltersConfiguration.defaultProps = {
  initialFilters: null,
  isEditMode: false,
  savedReportName: '',
};

export default ReportFiltersConfiguration;
